import React from 'react';
import { t, locale } from '../i18n';
import { Select } from '../ui';

// Noms de mois localisés (l'année importe peu : on prend une année quelconque).
const MONTHS = Array.from({ length: 12 }, (_, i) => {
    const name = new Date(2001, i, 1).toLocaleDateString(locale, { month: 'long' });
    return { value: i + 1, label: name.charAt(0).toUpperCase() + name.slice(1) };
});
// Jours max par mois — année non bissextile (la plage n'a pas d'année).
const DAYS_IN_MONTH = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
const pad = (n) => String(n).padStart(2, '0');

/**
 * Analyse la valeur stockée (`date_fermeture`) :
 *  - 'all_year'          -> ouvert toute l'année
 *  - 'DD/MM - DD/MM'     -> plage jour/mois (sans année)
 *  - '' / autre          -> non défini
 */
function parse(value) {
    if (value === 'all_year') return { allYear: true, from: null, to: null };
    const m = /^(\d{2})\/(\d{2})\s*-\s*(\d{2})\/(\d{2})$/.exec(value || '');
    if (!m) return { allYear: false, from: null, to: null };
    return {
        allYear: false,
        from: { day: +m[1], month: +m[2] },
        to: { day: +m[3], month: +m[4] },
    };
}

/** Reconstruit la valeur stockée depuis l'état du widget. */
function serialize(state) {
    if (state.allYear) return 'all_year';
    if (!state.from || !state.to) return '';
    return `${pad(state.from.day)}/${pad(state.from.month)} - ${pad(state.to.day)}/${pad(state.to.month)}`;
}

/**
 * Période d'ouverture d'un lieu : plage de dates jour/mois SANS année
 * (la saison se répète chaque année), ou « ouvert toute l'année ».
 * Émet vers le parent la chaîne au format historique (`date_fermeture`).
 */
export default function OpeningPeriod({ value, onChange }) {
    const state = parse(value);
    const from = state.from || { day: 1, month: 1 };
    const to = state.to || { day: 31, month: 12 };

    const toggleAllYear = (e) => {
        onChange(serialize(e.target.checked ? { allYear: true } : { allYear: false, from, to }));
    };

    const setPart = (which, field) => (e) => {
        const part = { ...(which === 'from' ? from : to), [field]: Number(e.target.value) };
        // Le jour ne peut pas dépasser le nombre de jours du mois choisi.
        const maxDay = DAYS_IN_MONTH[part.month - 1];
        if (part.day > maxDay) part.day = maxDay;
        onChange(serialize({
            allYear: false,
            from: which === 'from' ? part : from,
            to: which === 'to' ? part : to,
        }));
    };

    const dayOptions = (month) => Array.from({ length: DAYS_IN_MONTH[month - 1] }, (_, i) => i + 1);

    const dayMonth = (part, which) => (
        <span style={{ display: 'inline-flex', gap: 6 }}>
            <Select value={part.day} onChange={setPart(which, 'day')} style={{ width: 'auto' }} aria-label={t('jour')}>
                {dayOptions(part.month).map((d) => <option key={d} value={d}>{d}</option>)}
            </Select>
            <Select value={part.month} onChange={setPart(which, 'month')} style={{ width: 'auto' }} aria-label={t('mois')}>
                {MONTHS.map((mo) => <option key={mo.value} value={mo.value}>{mo.label}</option>)}
            </Select>
        </span>
    );

    return (
        <div>
            <label className="checkbox-row" style={{ marginBottom: 8 }}>
                <input type="checkbox" checked={state.allYear} onChange={toggleAllYear} />
                {t('all_year')}
            </label>

            {!state.allYear && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="text-muted text-sm">{t('du')}</span>
                    {dayMonth(from, 'from')}
                    <span className="text-muted text-sm">{t('au')}</span>
                    {dayMonth(to, 'to')}
                </div>
            )}

            <p className="text-muted text-sm" style={{ marginTop: 6, marginBottom: 0 }}>
                {t('periode_ouverture_aide')}
            </p>
        </div>
    );
}
